import { useEffect, useRef, useState } from 'react';
import { TrendingUp, Check } from 'lucide-react';
import { publicStudentWins } from '../lib/api';

/**
 * "OUR STUDENTS IMPROVE — STUDENT WINS" section.
 *
 * If the admin has uploaded student-feedback screenshots via the admin panel,
 * we render those (premium framed cards inside an infinite marquee — same
 * style as before, just with real images and no clickable link).
 *
 * If no screenshots are uploaded yet, we fall back to the original generated
 * PnL win cards so the page never looks empty.
 */

const FALLBACK_WINS_A = [
  { pair: 'XAUUSD', pct: '+4.2%', usd: '+$1,840', note: 'London breakout', who: 'Rafi · BD', flag: '🇧🇩' },
  { pair: 'EURUSD', pct: '+2.1%', usd: '+$612', note: 'NY retrace', who: 'Karan · IN', flag: '🇮🇳' },
  { pair: 'BTCUSD', pct: '+8.3%', usd: '+$3,210', note: '4H continuation', who: 'Ali · PK', flag: '🇵🇰' },
  { pair: 'SOL/USDT', pct: '+12.7%', usd: '+$2,430', note: 'Memecoin swing', who: 'Nayeem · BD', flag: '🇧🇩' },
  { pair: 'USDJPY', pct: '+1.6%', usd: '+$418', note: 'Scalp', who: 'Daniel · US', flag: '🇺🇸' },
  { pair: 'TSLA', pct: '+6.1%', usd: '+$1,250', note: 'Earnings play', who: 'Anna · UK', flag: '🇬🇧' },
  { pair: 'XAUUSD', pct: '+3.4%', usd: '+$960', note: 'Asia open', who: 'Mia · MY', flag: '🇲🇾' },
];

const FALLBACK_WINS_B = [
  { pair: 'GBPJPY', pct: '+2.7%', usd: '+$745', note: 'Swing setup', who: 'Shahin · AE', flag: '🇦🇪' },
  { pair: 'AAPL', pct: '+4.9%', usd: '+$1,520', note: 'Positional', who: 'Omar · SA', flag: '🇸🇦' },
  { pair: 'NAS100', pct: '+3.8%', usd: '+$2,100', note: 'Trend continuation', who: 'Vikram · IN', flag: '🇮🇳' },
  { pair: 'ETHUSD', pct: '+9.4%', usd: '+$1,880', note: 'Liquidity sweep', who: 'Jenny · PH', flag: '🇵🇭' },
  { pair: 'XAUUSD', pct: '+5.1%', usd: '+$2,310', note: 'Fed pivot', who: 'Rohan · IN', flag: '🇮🇳' },
  { pair: 'GBPUSD', pct: '+1.9%', usd: '+$540', note: 'NY scalp', who: 'Tariq · PK', flag: '🇵🇰' },
  { pair: 'NVDA', pct: '+7.2%', usd: '+$1,640', note: 'Breakout', who: 'Sam · CA', flag: '🇨🇦' },
];

const COUNTRY_FLAGS = {
  BD: '🇧🇩', IN: '🇮🇳', PK: '🇵🇰', US: '🇺🇸', GB: '🇬🇧', UK: '🇬🇧',
  AE: '🇦🇪', SA: '🇸🇦', PH: '🇵🇭', MY: '🇲🇾', CA: '🇨🇦', ID: '🇮🇩',
  NG: '🇳🇬', EG: '🇪🇬', BR: '🇧🇷', TR: '🇹🇷',
};

/* --- Synthetic PnL fallback card (kept for when admin hasn't uploaded yet) --- */
function FallbackWinCard({ w }) {
  return (
    <div className="relative shrink-0 w-[230px] sm:w-[260px] rounded-2xl card-surface gold-hairline overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-[rgba(245,185,66,0.06)] border-b border-[rgba(245,185,66,0.18)]">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(43_92%_56%)]">
          <TrendingUp className="h-3 w-3" /> AlveeTrade
        </div>
        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[rgba(34,199,132,0.12)] text-[hsl(152_78%_55%)] border border-[rgba(34,199,132,0.32)]">
          <Check className="h-2.5 w-2.5" /> Verified
        </span>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{w.pair}</span>
          <span className="text-[10px] text-muted-foreground">{w.note}</span>
        </div>
        <div className="font-display uppercase text-3xl sm:text-4xl text-[hsl(152_78%_55%)] leading-none">{w.pct}</div>
        <div className="font-mono text-base text-white mt-1 tabular-nums">{w.usd}</div>
        <div className="mt-3 relative h-12 rounded-md overflow-hidden bg-[rgba(255,255,255,0.02)]">
          <svg viewBox="0 0 200 50" preserveAspectRatio="none" className="w-full h-full">
            <defs>
              <linearGradient id="winFill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="rgba(34,199,132,0.45)" />
                <stop offset="100%" stopColor="rgba(34,199,132,0)" />
              </linearGradient>
            </defs>
            <path d="M0,40 L20,38 L35,32 L52,34 L70,28 L90,22 L108,24 L130,16 L150,18 L170,10 L200,5 L200,50 L0,50 Z" fill="url(#winFill)" />
            <path d="M0,40 L20,38 L35,32 L52,34 L70,28 L90,22 L108,24 L130,16 L150,18 L170,10 L200,5" fill="none" stroke="hsl(152 78% 55%)" strokeWidth="1.6" />
          </svg>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <span className="h-7 w-7 rounded-full bg-[rgba(245,185,66,0.12)] border border-[rgba(245,185,66,0.28)] flex items-center justify-center text-xs">{w.flag}</span>
          <div className="text-xs text-[hsl(0_0%_86%)] truncate">{w.who}</div>
        </div>
      </div>
    </div>
  );
}

/* --- Real student-screenshot card (image-based, no link) --- */
function ScreenshotCard({ w }) {
  const flag = w.country_code ? COUNTRY_FLAGS[w.country_code.toUpperCase()] || '🌐' : null;
  return (
    <div className="relative shrink-0 w-[230px] sm:w-[260px] rounded-2xl card-surface gold-hairline overflow-hidden select-none">
      {/* Phone-style header strip — same brand chrome as the synthetic cards */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[rgba(245,185,66,0.06)] border-b border-[rgba(245,185,66,0.18)]">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[hsl(43_92%_56%)]">
          <TrendingUp className="h-3 w-3" /> AlveeTrade
        </div>
        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[rgba(34,199,132,0.12)] text-[hsl(152_78%_55%)] border border-[rgba(34,199,132,0.32)]">
          <Check className="h-2.5 w-2.5" /> Verified
        </span>
      </div>

      {/* Screenshot fills the card */}
      <div className="relative bg-black">
        <img
          src={w.image_url}
          alt={w.caption || w.person || 'Student feedback'}
          className="w-full h-auto max-h-[420px] object-cover object-top pointer-events-none"
          loading="lazy"
          draggable={false}
        />
      </div>

      {(w.caption || w.person || flag) && (
        <div className="p-3 flex items-center gap-2 border-t border-[rgba(255,255,255,0.06)]">
          {flag && (
            <span className="h-7 w-7 rounded-full bg-[rgba(245,185,66,0.12)] border border-[rgba(245,185,66,0.28)] flex items-center justify-center text-xs flex-shrink-0">
              {flag}
            </span>
          )}
          <div className="min-w-0 flex-1">
            {w.caption && (
              <div className="text-[11px] text-[hsl(0_0%_88%)] line-clamp-2 leading-snug">{w.caption}</div>
            )}
            {w.person && (
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-0.5 truncate">{w.person}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Marquee({ items, direction = 'left', durationSec = 60, useScreenshots }) {
  const looped = [...items, ...items];
  const scrollRef = useRef(null);
  const [hint, setHint] = useState(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let rafId = 0;
    let lastUserInteract = 0;
    let lastFrame = performance.now();
    // Float accumulator — scrollLeft itself rounds to int on each write,
    // so for slow auto-scroll (a few px/s) we'd lose all motion otherwise.
    let scrollAcc = 0;
    const RESUME_DELAY = 1400;

    function pxPerSec() {
      const half = el.scrollWidth / 2;
      if (!half) return 0;
      return half / durationSec;
    }

    function tick(now) {
      const dt = Math.min((now - lastFrame) / 1000, 0.1);
      lastFrame = now;
      const half = el.scrollWidth / 2;
      if (half) {
        const idle = Date.now() - lastUserInteract > RESUME_DELAY;
        if (idle) {
          const sign = direction === 'left' ? 1 : -1;
          scrollAcc += pxPerSec() * dt * sign;
          // wrap accumulator into [0, half) range for left, (-half, 0] for right
          if (direction === 'left') {
            if (scrollAcc >= half) scrollAcc -= half;
            if (scrollAcc < 0) scrollAcc += half;
          } else {
            if (scrollAcc <= 0) scrollAcc += half;
            if (scrollAcc > half) scrollAcc -= half;
          }
          el.scrollLeft = scrollAcc;
        } else {
          // Sync accumulator to current scrollLeft so when we resume, we continue
          // smoothly from wherever the user dragged to.
          scrollAcc = el.scrollLeft;
        }
      }
      rafId = requestAnimationFrame(tick);
    }

    // Position at correct start: right-moving rows start at the loop's midpoint
    // so they have room to scroll backwards before wrapping.
    requestAnimationFrame(() => {
      const half = el.scrollWidth / 2;
      if (half) {
        if (direction === 'right') { el.scrollLeft = half - 1; scrollAcc = half - 1; }
        else { el.scrollLeft = 0; scrollAcc = 0; }
      }
    });

    rafId = requestAnimationFrame(tick);

    function markInteract() {
      lastUserInteract = Date.now();
      setHint(false);
    }

    // --- Mouse drag-to-scroll (native scroll handles touch + wheel already) ---
    let isDragging = false;
    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragMoved = false;

    function onPointerDown(e) {
      // Only handle primary button; ignore right-click etc.
      if (e.button !== undefined && e.button !== 0) return;
      // For touch / pen, native scrolling is smoother — let the browser handle it.
      if (e.pointerType && e.pointerType !== 'mouse') {
        markInteract();
        return;
      }
      isDragging = true;
      dragMoved = false;
      dragStartX = e.clientX;
      dragStartScroll = el.scrollLeft;
      try { el.setPointerCapture(e.pointerId); } catch (_) {}
      markInteract();
    }
    function onPointerMove(e) {
      if (!isDragging) return;
      const dx = e.clientX - dragStartX;
      if (Math.abs(dx) > 3) dragMoved = true;
      el.scrollLeft = dragStartScroll - dx;
      markInteract();
    }
    function onPointerUp(e) {
      if (!isDragging) return;
      isDragging = false;
      try { el.releasePointerCapture(e.pointerId); } catch (_) {}
      markInteract();
    }
    function onClickCapture(e) {
      // Suppress the click that follows a drag so we don't accidentally open a card link.
      if (dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        dragMoved = false;
      }
    }

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    el.addEventListener('pointerleave', onPointerUp);
    el.addEventListener('click', onClickCapture, true);

    // Generic interaction signals — these capture native touch / wheel etc.
    const passive = ['touchstart', 'touchmove', 'wheel'];
    passive.forEach((ev) => el.addEventListener(ev, markInteract, { passive: true }));

    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('pointerleave', onPointerUp);
      el.removeEventListener('click', onClickCapture, true);
      passive.forEach((ev) => el.removeEventListener(ev, markInteract));
    };
  }, [direction, durationSec, items]);

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        data-testid={`student-wins-marquee-${direction}`}
        className="relative overflow-x-auto overflow-y-hidden no-scrollbar cursor-grab active:cursor-grabbing"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        <div className="flex gap-3 sm:gap-4 w-max items-stretch px-2 sm:px-3">
          {looped.map((w, i) =>
            useScreenshots
              ? <ScreenshotCard key={`${direction}-${i}`} w={w} />
              : <FallbackWinCard key={`${direction}-${i}`} w={w} />
          )}
        </div>
      </div>
      {/* Edge gradient fades on top of scroll container */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-20 z-10 bg-gradient-to-r from-[hsl(240_6%_7%)] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-20 z-10 bg-gradient-to-l from-[hsl(240_6%_7%)] to-transparent" />
      {/* Subtle "swipe" hint shown on first row only */}
      {hint && direction === 'left' && (
        <div className="pointer-events-none absolute bottom-1 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-[0.24em] text-[hsl(43_92%_56%)]/60">
          ← Swipe to browse →
        </div>
      )}
    </div>
  );
}

export default function StudentWins() {
  const [items, setItems] = useState(null); // null = loading; [] = none; [...] = real
  useEffect(() => {
    let cancel = false;
    publicStudentWins()
      .then((d) => { if (!cancel) setItems(d.items || []); })
      .catch(() => { if (!cancel) setItems([]); });
    return () => { cancel = true; };
  }, []);

  // Decide what to render:
  //  - real screenshots if admin has uploaded any
  //  - otherwise the original synthetic fallback rows
  const useScreenshots = Array.isArray(items) && items.length > 0;
  let rowA = FALLBACK_WINS_A;
  let rowB = FALLBACK_WINS_B;
  if (useScreenshots) {
    // Split admin uploads into two roughly equal rows for the dual-marquee layout.
    // If there's only one item, both rows just loop it.
    if (items.length === 1) {
      rowA = items;
      rowB = items;
    } else {
      const mid = Math.ceil(items.length / 2);
      rowA = items.slice(0, mid);
      rowB = items.slice(mid).length ? items.slice(mid) : items.slice(0, mid);
    }
  }

  return (
    <section
      data-testid="student-wins-section"
      className="section-pad bg-[hsl(240_6%_7%)] border-t border-b border-border"
    >
      <div className="container-x">
        <div className="text-center max-w-3xl mx-auto mb-6 sm:mb-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)] mb-2">
            Our Students Improve
          </div>
          <h2 className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
            STUDENT WINS INSIDE ALVEETRADE
          </h2>
          <p className="text-muted-foreground mt-3 text-sm sm:text-base">
            Real trades. Real PnL. Posted live every single day inside the community.
          </p>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <Marquee items={rowA} direction="left" durationSec={70} useScreenshots={useScreenshots} />
        <Marquee items={rowB} direction="right" durationSec={85} useScreenshots={useScreenshots} />
      </div>
    </section>
  );
}
